import { MouseEventHandler, useState } from "react";
import { Box, useDisclosure } from "@chakra-ui/react";

import { useTranslator } from "@eo-locale/react";
import isEmpty from "lodash/isEmpty";

import { Menu, AlertDialog, FiMoreVertical } from "@/storybook-components";
import { useAppSelector } from "@/redux/store/hooks";

const ActionList = ({ menuList, currentData }): JSX.Element => {
  const { onClose } = useDisclosure();
  const [isAlertOpen, setAlertOpen] = useState(false);
  const [currentAlertContent, setCurrentAlertContent] = useState(null);
  const { translate } = useTranslator();
  const { type: loggedInUserType } = useAppSelector(
    (state) => state.user?.registeredUser?.profile
  );

  // to open and close alert dialog based on menu item click
  const openAlert = (alertContent) => {
    onClose();
    setAlertOpen(true);
    setCurrentAlertContent({ ...alertContent, currentData });
  };

  const closeAlert = () => {
    setAlertOpen(false);
  };

  const menuItemClickHandler = (item: {
    hasAlert: boolean;
    alertContent: object;
    actionHandler: (e) => MouseEventHandler<HTMLButtonElement>;
  }) => {
    if (item.hasAlert) openAlert(item.alertContent);
    else item.actionHandler(currentData);
  };

  // iterate through each menu item
  if (!menuList || menuList.length === 0) {
    return null;
  }
  const menuItems = menuList.map((menuItem, index) => {
    if (!menuItem.isItemVisible) return null;

    const isUserSpecific =
      menuItem.type === "edit" || menuItem.type === "delete";
    const isLoggedInUser = currentData?.author === menuItem.loggedInUser;
    const isMatchingUserType = currentData?.userType === loggedInUserType;
    // loggedInUser is the `login` name of signed user

    if (
      (isUserSpecific && isLoggedInUser && isMatchingUserType) ||
      !isUserSpecific
    ) {
      return (
        <Menu.Item
          key={index}
          icon={menuItem.icon}
          onClick={() => menuItemClickHandler(menuItem)}
          color={menuItem.menuItemColor || "gray.800"}
          isDisabled={menuItem.isDisabled}
          aria-label={menuItem.menuListLabel}
        >
          {menuItem.title}
        </Menu.Item>
      );
    }
    return null;
  });

  // return the menu on vertical dots Icon click & pop alert dialog
  return (
    <Box>
      {menuList.length === 1 && isEmpty(menuList[0]) ? (
        <FiMoreVertical size={24} />
      ) : (
        <Menu
          ariaLabelVerticalDotsIcon={`${translate("common.verticalDotsIcon")}`}
          menuAriaLabel={`${translate("common.actions")}`}
          menuButtonAriaLabel={`${translate("participantHeader.openMenu")}`}
        >
          {menuItems}
        </Menu>
      )}

      {currentAlertContent && (
        <AlertDialog
          isOpen={isAlertOpen}
          onClose={closeAlert}
          {...currentAlertContent}
          onPrimaryClickHandler={() => {
            if (currentAlertContent.onPrimaryClickHandler) {
              currentAlertContent.onPrimaryClickHandler(currentData);
            }
            closeAlert();
          }}
          onSecondaryClickHandler={() => {
            if (currentAlertContent.onSecondaryClickHandler) {
              currentAlertContent.onSecondaryClickHandler(currentData);
            }
            closeAlert();
          }}
        />
      )}
    </Box>
  );
};

export default ActionList;
